import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { ApiGatewayService } from '@http/apiGateway.service';
import { AppState } from 'src/app';

@Injectable()
export class PortfolioService {
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;

  constructor(
    private store: Store<AppState>,
    private apiGatewayService: ApiGatewayService,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.get('api')),
        take(1),
      )
      .subscribe((api: string) => (this.gbmAPI = api));
  }

  getTransactions(info: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetTransactions`,
      info,
    );
  }

  /**
   * Get initial position in specific {proccesDate}
   */
  getInitialPosition(contractId: string, processDate: string): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetInitialPositionSummary`,
      {
        contractId,
        processDate,
      },
    );
  }

  /**
   * Get close position in specific {processDate}
   */
  getClosePosition(contractId: string, processDate: string): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetClosePositionSummary`,
      {
        contractId,
        processDate,
      },
    );
  }

  /**
   * Get historical closure position in specific {processDate} of {contractId}
   *
   * @param contractId The user's contract ID
   * @param startDate Initial date.
   * @param endDate Final date.
   * @param [issueId] (Optional) The related issue ID
   */
  getHistoricalClosurePosition(
    contractId: string,
    startDate: string,
    endDate: string,
    issueId: string = '',
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetHistoricalClosurePosition`,
      {
        contractId,
        issueId,
        startDate,
        endDate,
      },
    );
  }

  getAforosShortSell(): Observable<any> {
    return this.apiGatewayService.get(
      `${this.gbmAPI}api/Portfolio/GetAforosVC`,
    );
  }

  /**
   * Get contract Yield by range in specific contractId
   * @param info Contains: contractId, beginDate, endDate and param of filter
   */
  getportfolioYield(info: any): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetContractYieldByRange`,
      info,
    );
  }

  getPositionSummary(
    contractId: string,
    isTimer?: boolean,
    isRealTime?: boolean,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetPositionSummary`,
      { request: contractId, isRealTime },
      isTimer,
    );
  }

  /**
   * Get the historical portfolio position of specific {contractId} for chart usage
   *
   * @param contractId The user's contract ID
   * @param startDate Initial date.
   * @param endDate Final date.
   */
  getHistoricalPortfolioPosition(
    contractId: string,
    startDate: string,
    endDate: string,
  ): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetHistoricalPortfolioPosition`,
      {
        contractId,
        startDate,
        endDate,
      },
    );
  }

  /**
   * Get the time weighted return of specific {contractId}
   *
   * @param contractId The user's contract ID
   */
  getTimeWeightedReturn(contractId: string): Observable<any> {
    return this.apiGatewayService.post(
      `${this.gbmAPI}api/Portfolio/GetTimeWeightedReturn`,
      {
        request: contractId,
      },
    );
  }
}
